<form [formGroup]="form" (ngSubmit)="saveForecastSettings()" #ngForm="ngForm">
  <aux-modal
    primaryButtonType="submit"
    [loading]="submitting()"
    [primaryButtonDisabled]="submittingButtonDisabled()"
    [primaryButtonTooltip]="submittingButtonTooltip()"
    [closeButtonDisabled]="submitting()"
    [customCloseAction]="closeModal"
    class="flex h-[688px]"
  >
    <div>
      <div class="rounded-[3px] bg-aux-warn2 px-[25px] py-[15px] font-bold">
        <p>
          These changes will override all existing driver settings for activities in selected
          categories for selected vendors
        </p>
      </div>

      <div class="my-4">
        <p class="mb-1 text-xs text-aux-gray-lightest">Vendor Contracts</p>
        <aux-multiple-vendor-select
          [(appliedSelectedVendorIDs)]="selectedVendorIDs"
          [itemsToDisplay]="3"
          [applySelectionOnButton]="false"
          [disableNoBudgetVendors]="true"
          [disableManualForecastVendors]="true"
        />
        <aux-quick-forecast-cost-category-info-block class="mt-[4px] block" />
      </div>

      @let noBudgetVendors = !selectedVendorIDs().length;

      @if (noBudgetVendors) {
        <div
          class="mt-8 flex flex-col items-center space-y-1 text-lg font-semibold text-aux-gray-darkest"
        >
          <aux-icon name="AlertTriangle" />
          <p>Please select a Vendor Contract</p>
        </div>
      } @else {
        <section auxSkeletonLoader [loading]="vendorsAmountTypesQuery.isLoading()">
          <div class="mb-4 mt-5 flex items-center space-x-2">
            <h3>Select cost category/categories</h3>
            @if (isCategoryIncorrect()) {
              <div class="flex items-center text-aux-red-dark">
                <aux-icon name="AlertTriangle" class="mr-2" />
                <p>{{ oneOfValidationMessage }}</p>
              </div>
            }
          </div>

          <div class="flex flex-col space-y-4">
            @if (isCategoryExists(amountTypes.AMOUNT_SERVICE)) {
              <div>
                <aux-checkbox-control formControlName="applyServices" [id]="'services'">
                  Services
                </aux-checkbox-control>
              </div>
            }

            @if (isCategoryExists(amountTypes.AMOUNT_PASSTHROUGH)) {
              <div>
                <aux-checkbox-control formControlName="applyPassThrough" [id]="'passThrough'">
                  Pass-through
                </aux-checkbox-control>
              </div>
            }

            @if (isCategoryExists(amountTypes.AMOUNT_INVESTIGATOR)) {
              <div>
                <aux-checkbox-control formControlName="applyInvestigator" [id]="'investigator'">
                  Investigator
                </aux-checkbox-control>
              </div>
            }
          </div>

          <div class="my-8 border-t border-aux-gray-dark"></div>
        </section>

        @if (isAnyActivitySelected) {
          <div class="mb-4 flex items-center space-x-2">
            <h3>Select a driver</h3>

            @if (isDriverIncorrect) {
              <div class="section-error flex items-center text-aux-red-dark">
                <aux-icon name="AlertTriangle" class="mr-2" />
                <p>Please make selections before saving.</p>
              </div>
            }
          </div>

          <aux-radio-button-group
            formControlName="driver"
            display="column"
            label="Driver"
            name="driver"
          >
            <aux-radio-button
              [id]="'custom-driver'"
              value="custom"
              label="Custom"
              [disabled]="!customCurves.length"
              [tooltip]="customCurves.length ? '' : 'No Custom Curves exist.'"
            />
            <aux-radio-button value="patient" label="Patient" [id]="'patient-driver'" />
            <aux-radio-button value="site" label="Site" [id]="'site-driver'" />
            <aux-radio-button value="time" label="Time" [id]="'time-driver'" />
          </aux-radio-button-group>

          <div class="mt-8 w-full">
            @switch (form.get('driver')!.value!) {
              @case ('patient') {
                <ng-container *ngTemplateOutlet="patientCurveDriver" />
              }
              @case ('site') {
                <ng-container *ngTemplateOutlet="siteDriver" />
              }
              @case ('time') {
                <ng-container *ngTemplateOutlet="timeDriver" />
              }
              @case ('custom') {
                <ng-container *ngTemplateOutlet="customDriver" />
              }
            }
          </div>
        }
      }
    </div>
  </aux-modal>

  <!-- Patient Driver Form -->
  <ng-template #patientCurveDriver>
    <div>
      <div class="mb-1 text-xs">Patient curve</div>
      <aux-patient-curves-dropdown
        controlName="patientCurve"
        [curves]="patientCurveQuery.patientCurves()"
        name="Patient curve"
        [curveControl]="form.get('patientCurve')!"
      />
    </div>
  </ng-template>

  <!-- Site Driver Form -->
  <ng-template #siteDriver>
    <div class="flex flex-col space-y-4">
      <div>
        <div class="mb-1 text-xs">Period</div>
        <ng-select
          formControlName="siteDriverPeriod"
          [items]="periodSiteOptions"
          [clearable]="false"
          auxFormError
          bindValue="value"
          placeholder="Select"
          label="Period"
          (change)="revalidateSiteCurveField()"
          [appendTo]="'body'"
        />
      </div>

      <div>
        <div class="mb-1 text-xs">Site curve</div>
        <aux-patient-curves-dropdown
          controlName="siteCurve"
          [curves]="siteCurves()"
          name="Site curve"
          [curveControl]="form.get('siteCurve')!"
        />
      </div>
    </div>
  </ng-template>

  <!-- Time Driver Form -->
  <ng-template #timeDriver>
    <div class="mb-2 flex items-center space-x-2">
      <h3>Select a time period</h3>

      @if (showTimeFormEmptyWarning) {
        <div class="section-error flex items-center text-aux-red-dark">
          <aux-icon name="AlertTriangle" class="mr-2" />
          <p>Please make selections before saving.</p>
        </div>
      }
    </div>

    <div class="grid grid-cols-2 gap-4 pb-12">
      <div class="col-span-2">
        <aux-radio-button-group
          formControlName="milestone"
          display="column"
          label="Milestone"
          name="milestone"
        >
          @for (milestone of milestonePhases(); track milestone.id) {
            @let formatterStartDate = milestone.contractStartDate | date: 'longDate';
            @let formatterEndDate = milestone.contractEndDate | date: 'longDate';
            @let subLabel = formatterStartDate + ' - ' + formatterEndDate;

            <aux-radio-button
              [id]="milestone.id"
              [value]="milestone.id"
              [label]="milestone.categoryName"
              [subLabel]="subLabel || ''"
            />
          }
          <aux-radio-button
            value="custom"
            label="Custom"
            [id]="'custom-milestone-radio'"
            class="!font-medium"
          />
        </aux-radio-button-group>
      </div>

      @if (form.controls.milestone.value === 'custom') {
        <div>
          <p class="mb-1 text-xs">Starting milestone</p>
          <ng-select
            [items]="this.milestones()"
            formControlName="startMilestone"
            [searchable]="false"
            auxFormError
            placeholder="Select"
            (change)="
              disableMilestonesByCondition($event, 'startMilestone', 'endMilestone');
              revalidateMilestoneField('endMilestone')
            "
            [appendTo]="'body'"
            class="[&_.ng-input]:hidden"
          >
            <ng-template ng-label-tmp let-item="item">
              <ng-container
                [ngTemplateOutlet]="startMilestone"
                [ngTemplateOutletContext]="{ item }"
              />
            </ng-template>

            <ng-template ng-option-tmp let-item="item">
              <ng-container
                [ngTemplateOutlet]="startMilestone"
                [ngTemplateOutletContext]="{ item }"
              />
            </ng-template>

            <ng-template let-item="item" #startMilestone>
              <div
                class="flex items-center space-x-2 before:absolute before:inset-0 before:content-['']"
                [auxTooltip]="getTimelineStartEndMilestoneTooltip(item, item.contractStartDate)"
                auxTooltipPanelContainerClass="quick-forecast-modal-start-milestone-tooltip"
                auxTooltipZIndex="1500"
              >
                <span class="max-w-[70%] overflow-hidden text-ellipsis font-bold">
                  {{ item.milestoneName }}
                </span>
                <span>{{ item.contractStartDate | date: 'longDate' }}</span>
              </div>
            </ng-template>
          </ng-select>
        </div>

        <div>
          <p class="mb-1 text-xs">Ending milestone</p>
          <ng-select
            [items]="this.milestones()"
            formControlName="endMilestone"
            [searchable]="false"
            auxFormError
            placeholder="Select"
            (change)="
              disableMilestonesByCondition($event, 'endMilestone', 'startMilestone');
              revalidateMilestoneField('startMilestone')
            "
            [appendTo]="'body'"
            class="[&_.ng-input]:hidden"
          >
            <ng-template ng-label-tmp let-item="item">
              <ng-container
                [ngTemplateOutlet]="endingMilestone"
                [ngTemplateOutletContext]="{ item }"
              />
            </ng-template>

            <ng-template ng-option-tmp let-item="item">
              <ng-container
                [ngTemplateOutlet]="endingMilestone"
                [ngTemplateOutletContext]="{ item }"
              />
            </ng-template>

            <ng-template let-item="item" #endingMilestone>
              <div
                class="flex items-center space-x-2 before:absolute before:inset-0 before:content-['']"
                [auxTooltip]="getTimelineStartEndMilestoneTooltip(item, item.contractEndDate)"
                auxTooltipPanelContainerClass="quick-forecast-modal-ending-milestone-tooltip"
                auxTooltipZIndex="1500"
              >
                <span class="max-w-[70%] overflow-hidden text-ellipsis font-bold">
                  {{ item.milestoneName }}
                </span>
                <span>{{ item.contractEndDate | date: 'longDate' }}</span>
              </div>
            </ng-template>
          </ng-select>
        </div>

        <span class="col-span-2">OR</span>

        <aux-date-picker
          [control]="form.controls.customStartMilestone"
          label="Custom start date"
          (blured)="
            disableMilestonesByCondition($event, 'customStartMilestone', 'customEndMilestone')
          "
        />

        <aux-date-picker
          [control]="form.controls.customEndMilestone"
          label="Custom end date"
          (blured)="
            disableMilestonesByCondition($event, 'customEndMilestone', 'customStartMilestone');
            revalidateMilestoneField('customStartMilestone')
          "
        />

        <div class="col-span-2 flex items-center space-x-2 text-aux-black">
          <aux-icon name="AlertTriangle" />
          <p>Custom start and end dates will not dynamically update with your timeline.</p>
        </div>
      }

      <div class="col-span-2 mt-6">
        <h3 class="mb-2">Select method</h3>
        <div>
          <p class="mb-1 text-xs">Method</p>
          <ng-select
            formControlName="timeMethod"
            [items]="methodTimeOptions"
            [clearable]="false"
            dropdownPosition="top"
            auxFormError
            bindValue="value"
            placeholder="Select"
            [appendTo]="'body'"
          />
        </div>
      </div>
    </div>
  </ng-template>

  <!-- Custom Driver Form -->
  <ng-template #customDriver>
    <div>
      <div class="mb-1 text-xs">Custom curve</div>
      <ng-select
        formControlName="customCurve"
        [items]="customCurves"
        [clearable]="false"
        auxFormError
        bindValue="value"
        bindLabel="name"
        placeholder="Select"
        label="Custom curve"
        [appendTo]="'body'"
      />
    </div>
  </ng-template>
</form>
